/**
 * A Well Testing ticket's lines (GET /well-testing/tickets/:id → lines).
 *
 * Unpriced lines are FLAGGED on the row with an "Unpriced" chip and their
 * money reads `—`, never 0 — the office approve refuses while any remain
 * (`ticket_unpriced`). Adding, editing and deleting a line is offered only
 * while the ticket is editable (`editable`): a draft, or a signed ticket the
 * office has chosen to unlock (the screen asked first; the edit returns it to
 * draft). A drafted ('auto') line keeps its unit (`unit_fixed`).
 *
 * HOW A LINE IS PRICED
 *   from the rate card     `rate_id` (a third-party rate also takes the vendor's cost)
 *   the office's own rate  `kind` + `unit` + `rate`
 *   a third-party cost     `kind: third_party` + `unit` + `third_party_cost` (+ `markup_pct`) —
 *                          the API prices it; it never takes a typed `rate` (`third_party_rate`)
 *   no price yet           `kind` + `unit`; the line is unpriced
 */
import React, { useState } from 'react';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAddWtTicketLine, useRemoveWtTicketLine, useUpdateWtTicketLine, type WtTicketEditResult } from '../../../hooks/useWtTickets';
import { fmtCurrency } from '../../../lib/display-labels';
import { wtTicketRefusal } from '../../../lib/wt-tickets';
import { fmtWtNumber, wtDay, toInput } from '../../../lib/well-testing';
import type { WtRate, WtTicket, WtTicketCatalog, WtTicketLine, WtTicketLineCreate, WtTicketLinePatch } from '../../../types/wt-tickets';
import { WT_DATE_INPUT, WT_INPUT, WT_PRIMARY, WT_SECONDARY, WtEmpty, WtField, WtFormError, WtPanel } from '../WtBits';
import { PfInlineConfirm as WtInlineConfirm } from '../../pipeline-facility/digs/DigBits';
import { wtRateOption, wtRateText, ratesForTicket } from './TicketBits';

type PriceMode = 'keep' | 'card' | 'office' | 'vendor' | 'clear';

const isNum = (v: string) => v.trim() !== '' && Number.isFinite(Number(v));

/** The line form: add (no `line`) or edit. Sends only what changed on an edit. */
export function WtTicketLineForm({
  ticket,
  line,
  rates,
  catalog,
  pending,
  error,
  onCancel,
  onSubmit,
}: {
  ticket: WtTicket;
  line?: WtTicketLine;
  rates: WtRate[];
  catalog: WtTicketCatalog | null;
  pending: boolean;
  error: string | null;
  onCancel: () => void;
  onSubmit: (body: WtTicketLineCreate | WtTicketLinePatch) => void;
}) {
  const adding = !line;
  const thirdPartyLine = !!line && line.kind === 'third_party';
  const [description, setDescription] = useState(line?.description ?? '');
  const [quantity, setQuantity] = useState(toInput(line?.quantity));
  const [kind, setKind] = useState(line?.kind ?? '');
  const [unit, setUnit] = useState(line?.unit ?? '');
  const [volumeUnit, setVolumeUnit] = useState(line?.volumeUnit ?? '');
  const [mode, setMode] = useState<PriceMode>(adding ? 'card' : thirdPartyLine ? 'vendor' : 'keep');
  const [rateId, setRateId] = useState('');
  const [rate, setRate] = useState('');
  const [cost, setCost] = useState(toInput(line?.thirdPartyCost));
  const [markup, setMarkup] = useState(toInput(line?.markupPct));
  const [workDate, setWorkDate] = useState(ticket.scope === 'day' ? ticket.workDate ?? '' : '');
  const [problem, setProblem] = useState<string | null>(null);
  const unitFixed = !!line && line.source !== 'office';
  const effUnit = unit || null;
  const cardRates = ratesForTicket(rates, ticket, { unit: adding ? null : effUnit, on: ticket.workDate }).filter(
    // An edit re-prices a line of its own kind; a third-party rate prices a third-party line only (`rate_kind_mismatch`).
    (r) => adding || r.kind !== 'third_party',
  );
  const picked = cardRates.find((r) => r.id === rateId) ?? null;
  const pickedThirdParty = picked?.kind === 'third_party';
  const kinds = (catalog?.priceKinds ?? []).filter((k) => (mode === 'vendor' ? k.value === 'third_party' : k.value !== 'third_party'));
  const units = catalog?.priceUnits ?? [];

  const submit = () => {
    setProblem(null);
    if (!description.trim()) return setProblem('Enter what the line is for.');
    if (!isNum(quantity)) return setProblem('Enter the quantity as a number.');
    if (mode === 'card' && !rateId) return setProblem('Pick a rate from the rate card, or price the line another way.');
    if (mode === 'office' && !isNum(rate)) return setProblem('Enter the rate as a number.');
    if ((mode === 'vendor' || pickedThirdParty) && !isNum(cost)) return setProblem('Enter the vendor’s cost as a number.');
    if ((mode === 'vendor' || pickedThirdParty) && markup.trim() && !isNum(markup)) return setProblem('Enter the markup as a percentage, or leave it blank.');
    if (adding) {
      if (mode !== 'card' && mode !== 'vendor' && (!kind || !unit)) return setProblem('Pick what kind of charge it is and its unit.');
      if (mode === 'vendor' && !unit) return setProblem('Pick the unit the vendor’s cost is counted in.');
      if (mode !== 'card' && unit === 'per_volume' && !volumeUnit.trim()) return setProblem('Enter the volume unit (m³, bbl, …).');
      const body: WtTicketLineCreate = { description: description.trim(), quantity: quantity.trim() };
      if (mode === 'card') {
        body.rate_id = rateId;
        if (pickedThirdParty) {
          body.third_party_cost = cost.trim();
          if (markup.trim()) body.markup_pct = markup.trim();
        }
      } else {
        body.kind = mode === 'vendor' ? 'third_party' : kind;
        body.unit = unit;
        if (unit === 'per_volume') body.volume_unit = volumeUnit.trim();
        if (mode === 'office') body.rate = rate.trim();
        if (mode === 'vendor') {
          body.third_party_cost = cost.trim();
          if (markup.trim()) body.markup_pct = markup.trim();
        }
      }
      if (ticket.scope === 'job' && workDate) body.work_date = workDate;
      return onSubmit(body);
    }
    const body: WtTicketLinePatch = {};
    if (description.trim() !== line!.description) body.description = description.trim();
    if (Number(quantity) !== line!.quantity) body.quantity = quantity.trim();
    if (!unitFixed && unit && unit !== line!.unit) body.unit = unit;
    if (thirdPartyLine) {
      if (Number(cost) !== line!.thirdPartyCost) body.third_party_cost = cost.trim();
      const m = markup.trim() === '' ? 0 : Number(markup);
      if (m !== (line!.markupPct ?? 0)) body.markup_pct = markup.trim() === '' ? null : markup.trim();
    }
    if (mode === 'card') body.rate_id = rateId;
    if (mode === 'office') body.rate = rate.trim();
    if (mode === 'clear') body.rate = null;
    if (!Object.keys(body).length) return setProblem('Nothing has changed.');
    return onSubmit(body);
  };

  const vendorFields = (
    <>
      <WtField label={`Vendor’s cost (${ticket.currency ?? '—'}) *`} hint="Per unit, before any markup.">
        <input className={WT_INPUT} inputMode="decimal" aria-label="Vendor’s cost" value={cost} onChange={(e) => setCost(e.target.value)} />
      </WtField>
      <WtField label="Markup %" hint={pickedThirdParty ? 'Blank = the rate’s own pricing.' : 'Blank = billed at cost.'}>
        <input className={WT_INPUT} inputMode="decimal" aria-label="Markup %" value={markup} onChange={(e) => setMarkup(e.target.value)} />
      </WtField>
    </>
  );

  return (
    <div data-testid={adding ? 'wt-ticket-line-add-form' : 'wt-ticket-line-edit-form'} className="space-y-3 border-t border-fieldiq-border bg-fieldiq-dark/40 px-4 py-4">
      <WtFormError message={problem ?? error} />
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <div className="sm:col-span-2">
          <WtField label="Description *">
            <input className={WT_INPUT} value={description} maxLength={300} onChange={(e) => setDescription(e.target.value)} />
          </WtField>
        </div>
        <WtField label="Quantity *">
          <input className={WT_INPUT} inputMode="decimal" aria-label="Quantity" value={quantity} onChange={(e) => setQuantity(e.target.value)} />
        </WtField>
        {adding && ticket.scope === 'job' && (
          <WtField label="Work day" hint="Optional.">
            <input className={WT_DATE_INPUT} type="date" aria-label="Work day" value={workDate} onChange={(e) => setWorkDate(e.target.value)} />
          </WtField>
        )}
      </div>
      <fieldset className="space-y-2">
        <legend className="mb-1 text-[0.62rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">Price</legend>
        {thirdPartyLine ? (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">{vendorFields}</div>
        ) : (
          <>
            <div className="flex flex-wrap gap-4 text-sm text-fieldiq-text">
              {!adding && (
                <label className="flex items-center gap-2">
                  <input type="radio" name="wt-price" checked={mode === 'keep'} onChange={() => setMode('keep')} /> Keep the price
                </label>
              )}
              <label className="flex items-center gap-2">
                <input type="radio" name="wt-price" checked={mode === 'card'} onChange={() => setMode('card')} /> From the rate card
              </label>
              <label className="flex items-center gap-2">
                <input type="radio" name="wt-price" checked={mode === 'office'} onChange={() => setMode('office')} /> The office’s own rate
              </label>
              {adding && (
                <label className="flex items-center gap-2">
                  <input type="radio" name="wt-price" checked={mode === 'vendor'} onChange={() => { setMode('vendor'); setKind('third_party'); }} /> A third-party cost
                </label>
              )}
              <label className="flex items-center gap-2">
                <input type="radio" name="wt-price" checked={mode === 'clear'} onChange={() => setMode('clear')} /> {adding ? 'No price yet' : 'Clear the price'}
              </label>
            </div>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              {mode === 'card' && (
                <div className="sm:col-span-2">
                  <WtField label="Rate" hint={adding ? 'The rate sets the kind and unit.' : 'Rates counted in this line’s unit.'}>
                    <select className={WT_INPUT} aria-label="Rate" value={rateId} onChange={(e) => setRateId(e.target.value)}>
                      <option value="">{cardRates.length ? 'Pick a rate' : 'No rate on the card fits this line'}</option>
                      {cardRates.map((r) => (
                        <option key={r.id} value={r.id}>
                          {wtRateOption(r)}
                        </option>
                      ))}
                    </select>
                  </WtField>
                </div>
              )}
              {mode === 'card' && pickedThirdParty && vendorFields}
              {mode === 'office' && (
                <WtField label={`Rate (${ticket.currency ?? '—'})`}>
                  <input className={WT_INPUT} inputMode="decimal" aria-label="Office rate" value={rate} onChange={(e) => setRate(e.target.value)} />
                </WtField>
              )}
              {mode === 'vendor' && vendorFields}
              {adding && mode !== 'card' && mode !== 'vendor' && (
                <WtField label="Kind *">
                  <select className={WT_INPUT} aria-label="Kind" value={kind} onChange={(e) => setKind(e.target.value)}>
                    <option value="">Pick one</option>
                    {kinds.map((k) => (
                      <option key={k.value} value={k.value}>
                        {k.label}
                      </option>
                    ))}
                  </select>
                </WtField>
              )}
              {(adding ? mode !== 'card' : !unitFixed) && (
                <WtField label={adding ? 'Unit *' : 'Unit'}>
                  <select className={WT_INPUT} aria-label="Unit" value={unit} onChange={(e) => setUnit(e.target.value)}>
                    <option value="">Pick one</option>
                    {units.map((u) => (
                      <option key={u.value} value={u.value}>
                        {u.label}
                      </option>
                    ))}
                  </select>
                </WtField>
              )}
              {adding && mode !== 'card' && unit === 'per_volume' && (
                <WtField label="Volume unit *">
                  <input className={WT_INPUT} maxLength={20} aria-label="Volume unit" value={volumeUnit} onChange={(e) => setVolumeUnit(e.target.value)} />
                </WtField>
              )}
            </div>
          </>
        )}
        {unitFixed && <p className="text-xs text-fieldiq-muted">A line drafted from the shift logs keeps the unit its days, shifts or hours were counted in.</p>}
      </fieldset>
      <div className="flex flex-wrap justify-end gap-2">
        <button type="button" className={WT_SECONDARY} onClick={onCancel} disabled={pending}>
          Cancel
        </button>
        <button type="button" className={WT_PRIMARY} onClick={submit} disabled={pending}>
          {pending ? 'Saving…' : adding ? 'Add line' : 'Save line'}
        </button>
      </div>
    </div>
  );
}

/** A line's quantity unit: km / mi on a distance line, the volume unit on a volume line, the unit's label otherwise. */
const qtyUnit = (l: WtTicketLine) =>
  l.unit === 'per_distance' ? l.distanceUnit ?? '' : l.unit === 'per_volume' ? l.volumeUnit ?? '' : l.unitLabel ?? l.unit;

export function WtTicketLines({
  ticket,
  editable,
  canWrite,
  rates,
  catalog,
  highlight,
  onEdited,
}: {
  ticket: WtTicket;
  /** Draft, or a signed ticket the office unlocked. */
  editable: boolean;
  canWrite: boolean;
  rates: WtRate[];
  catalog: WtTicketCatalog | null;
  /** Line ids an `ticket_unpriced` refusal named. */
  highlight: string[];
  onEdited: (r: WtTicketEditResult) => void;
}) {
  const add = useAddWtTicketLine();
  const update = useUpdateWtTicketLine();
  const remove = useRemoveWtTicketLine();
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const lines = ticket.lines ?? [];
  const cur = ticket.currency;
  const write = editable && canWrite;
  const draft = ticket.status === 'draft';

  const done = (msg: string) => (r: WtTicketEditResult) => {
    toast.success(msg);
    setAdding(false);
    setEditingId(null);
    setDeletingId(null);
    onEdited(r);
  };

  return (
    <WtPanel
      eyebrow="Lines"
      title={`${lines.length} line${lines.length === 1 ? '' : 's'}`}
      testId="wt-ticket-lines"
      actions={
        write && !adding ? (
          <button type="button" className={WT_SECONDARY} onClick={() => { setError(null); setEditingId(null); setAdding(true); }}>
            <Plus className="size-4" aria-hidden="true" />
            Add line
          </button>
        ) : undefined
      }
    >
      {(ticket.unpricedCount ?? 0) > 0 && (
        <p data-testid="wt-ticket-unpriced-count" className="border-b border-fieldiq-border bg-fi-signal-red/10 px-4 py-2 text-sm text-fieldiq-text">
          {ticket.unpricedCount === 1 ? 'One line has no rate.' : `${ticket.unpricedCount} lines have no rate.`} Price {ticket.unpricedCount === 1 ? 'it' : 'them'} or remove{' '}
          {ticket.unpricedCount === 1 ? 'it' : 'them'} before the office approves the ticket.
        </p>
      )}
      {adding && (
        <WtTicketLineForm
          ticket={ticket}
          rates={rates}
          catalog={catalog}
          pending={add.isPending}
          error={error}
          onCancel={() => setAdding(false)}
          onSubmit={(body) => {
            setError(null);
            add.mutate({ id: ticket.id, body: body as WtTicketLineCreate }, { onSuccess: done('Line added'), onError: (e) => setError(wtTicketRefusal(e, 'The line could not be added.')) });
          }}
        />
      )}
      {!lines.length ? (
        <WtEmpty>No lines. Refresh the ticket from the shift logs, or add a line.</WtEmpty>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[860px] text-sm">
            <thead>
              <tr className="border-b border-fieldiq-border text-left text-[0.62rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">
                <th className="px-4 py-2">Line</th>
                <th className="px-4 py-2">Day</th>
                <th className="px-4 py-2 text-right">Quantity</th>
                <th className="px-4 py-2 text-right">Rate</th>
                <th className="px-4 py-2 text-right">Amount</th>
                {write && <th className="px-4 py-2 text-right"><span className="sr-only">Actions</span></th>}
              </tr>
            </thead>
            <tbody>
              {lines.map((l) => (
                <React.Fragment key={l.id}>
                  <tr
                    data-testid="wt-ticket-line"
                    data-unpriced={l.unpriced ? 'true' : undefined}
                    className={`border-b border-fieldiq-border align-top ${l.unpriced ? 'bg-fi-signal-red/10' : ''} ${highlight.includes(l.id) ? 'outline outline-1 outline-fi-signal-red' : ''}`}
                  >
                    <td className="px-4 py-2">
                      <p className="font-semibold text-white">{l.description || '—'}</p>
                      <p className="mt-0.5 text-xs text-fieldiq-muted">
                        {l.kindLabel || l.kind || '—'}
                        {l.crewRoleLabel ? ` · ${l.crewRoleLabel}` : ''}
                        {l.shiftLabel ? ` · ${l.shiftLabel} shift` : ''} ·{' '}
                        <span data-testid="wt-ticket-line-source">{l.source === 'office' ? 'Added by the office' : 'From the shift logs'}</span>
                        {l.rateCode ? ` · ${l.rateCode}` : l.rateSource === 'office' ? ' · Office rate' : ''}
                      </p>
                      {l.thirdPartyCost !== null && (
                        <p className="mt-0.5 text-xs text-fieldiq-muted">
                          Vendor’s cost {fmtCurrency(l.thirdPartyCost, cur)}
                          {l.markupPct ? ` + ${l.markupPct}%` : ' at cost'}
                        </p>
                      )}
                      {l.unpriced && (
                        <p className="mt-1 flex flex-wrap items-center gap-2 text-xs text-fieldiq-text">
                          <span data-testid="wt-ticket-line-unpriced" className="rounded-full bg-fi-signal-red px-2 py-0.5 text-[0.62rem] font-black uppercase tracking-[0.08em] text-white">
                            Unpriced
                          </span>
                          {l.unpricedReason && <span>{l.unpricedReason}</span>}
                        </p>
                      )}
                    </td>
                    <td className="px-4 py-2">{wtDay(l.workDate)}</td>
                    <td className="px-4 py-2 text-right font-mono">
                      {fmtWtNumber(l.quantity)} {qtyUnit(l)}
                    </td>
                    <td className="px-4 py-2 text-right font-mono">{wtRateText(l.rate, cur, l.unit, l.distanceUnit, l.volumeUnit)}</td>
                    <td className="px-4 py-2 text-right font-mono">{fmtCurrency(l.amount, cur)}</td>
                    {write && (
                      <td className="px-4 py-2 text-right">
                        <div className="flex justify-end gap-1">
                          <button type="button" aria-label={`Edit ${l.description}`} className={WT_SECONDARY} onClick={() => { setError(null); setAdding(false); setEditingId(l.id); }}>
                            <Pencil className="size-4" aria-hidden="true" />
                          </button>
                          <button type="button" aria-label={`Delete ${l.description}`} className={WT_SECONDARY} onClick={() => { setError(null); setDeletingId(l.id); }}>
                            <Trash2 className="size-4" aria-hidden="true" />
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                  {write && deletingId === l.id && (
                    <tr>
                      <td colSpan={6} className="px-4 py-3">
                        <WtInlineConfirm
                          testId="wt-ticket-line-delete-confirm"
                          message={`Remove “${l.description}” from the ticket?${l.source === 'auto' ? ' A refresh from the shift logs will draw it again.' : ''}`}
                          confirmLabel="Remove line"
                          cancelLabel="Keep it"
                          pending={remove.isPending}
                          error={error}
                          onCancel={() => setDeletingId(null)}
                          onConfirm={() =>
                            remove.mutate({ id: ticket.id, lineId: l.id }, { onSuccess: done('Line removed'), onError: (e) => setError(wtTicketRefusal(e, 'The line could not be removed.')) })
                          }
                        />
                      </td>
                    </tr>
                  )}
                  {write && editingId === l.id && (
                    <tr>
                      <td colSpan={6} className="p-0">
                        <WtTicketLineForm
                          ticket={ticket}
                          line={l}
                          rates={rates}
                          catalog={catalog}
                          pending={update.isPending}
                          error={error}
                          onCancel={() => setEditingId(null)}
                          onSubmit={(body) =>
                            update.mutate(
                              { id: ticket.id, lineId: l.id, body: body as WtTicketLinePatch },
                              { onSuccess: done('Line saved'), onError: (e) => setError(wtTicketRefusal(e, 'The line could not be saved.')) },
                            )
                          }
                        />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <dl data-testid="wt-ticket-totals" className="grid gap-1 border-t border-fieldiq-border px-4 py-3 text-sm sm:ml-auto sm:w-80">
        {draft ? (
          <TotalRow label="Subtotal (priced lines)" value={fmtCurrency(ticket.draftSubtotal, cur)} />
        ) : (
          <>
            <TotalRow label="Subtotal" value={fmtCurrency(ticket.subtotal, cur)} />
            {ticket.taxRate1Label && <TotalRow label={ticket.taxRate1Label} value={fmtCurrency(ticket.taxRate1Amount, cur)} />}
            {ticket.taxRate2Label && <TotalRow label={ticket.taxRate2Label} value={fmtCurrency(ticket.taxRate2Amount, cur)} />}
            <TotalRow label="Total" value={fmtCurrency(ticket.totalAmount, cur)} strong />
          </>
        )}
        {draft && <p className="text-xs text-fieldiq-muted">Tax and the total are set when the office approves the ticket.</p>}
      </dl>
    </WtPanel>
  );
}

function TotalRow({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <div className="flex justify-between gap-4">
      <dt className="text-fieldiq-muted">{label}</dt>
      <dd className={`font-mono ${strong ? 'font-extrabold text-white' : 'text-fieldiq-text'}`}>{value}</dd>
    </div>
  );
}

